// Workspace versioning and comparison utilities

import { Workspace, WorkspaceVersion } from '@/types/workspace';
import { FailureFilters } from '@/types/filters';

interface WorkspaceChange {
  from: unknown;
  to: unknown;
}

interface WorkspaceComparison {
  hasChanges: boolean;
  changes: {
    name?: WorkspaceChange;
    description?: WorkspaceChange;
    filters?: unknown;
    analysisParams?: unknown;
    tags?: WorkspaceChange;
  };
  filtersChanged?: boolean;
  parametersChanged?: boolean;
  dataChanged?: boolean;
  metadataChanged?: boolean;
  changesSummary?: string[];
}

/**
 * Create a new version from a workspace
 */
export function createVersion(
  workspace: Workspace,
  changesSummary: string,
  userId: string,
  userName?: string
): WorkspaceVersion {
  // For mock users, provide a friendly name
  let displayName = userName;
  if (!displayName) {
    if (userId === 'mock_user_123') {
      displayName = 'Development User';
    } else {
      displayName = `User ${userId.slice(-4)}`; // Use last 4 chars of ID
    }
  }
  
  return {
    versionNumber: workspace.metadata.version,
    workspace: deepClone(workspace), // Deep copy of workspace state
    changesSummary,
    createdAt: new Date().toISOString(),
    createdBy: {
      id: userId,
      name: displayName,
    },
  };
}

/**
 * Compare two workspace configurations to detect changes
 */
export function compareWorkspaces(
  workspace1: Workspace,
  workspace2: Workspace
): WorkspaceComparison {
  const changes: WorkspaceComparison['changes'] = {};
  const changesSummary: string[] = [];

  // Compare name
  if (workspace1.name !== workspace2.name) {
    changes.name = {
      from: workspace1.name,
      to: workspace2.name,
    };
    changesSummary.push(`Name: "${workspace1.name}" → "${workspace2.name}"`);
  }

  // Compare description
  if (workspace1.description !== workspace2.description) {
    changes.description = {
      from: workspace1.description,
      to: workspace2.description,
    };
    changesSummary.push('Description updated');
  }

  // Compare filters
  const filtersChanged = !deepEquals(
    workspace1.configuration.filters,
    workspace2.configuration.filters
  );
  if (filtersChanged) {
    changes.filters = {
      from: workspace1.configuration.filters,
      to: workspace2.configuration.filters,
    };
    const filterChanges = compareFilters(
      workspace1.configuration.filters,
      workspace2.configuration.filters
    );
    changesSummary.push(...filterChanges);
  }

  // Compare analysis parameters
  const parametersChanged = !deepEquals(
    workspace1.configuration.analysisParams,
    workspace2.configuration.analysisParams
  );
  if (parametersChanged) {
    const paramChanges: Record<string, WorkspaceChange> = {};

    if (
      workspace1.configuration.analysisParams.showConfidenceIntervals !==
      workspace2.configuration.analysisParams.showConfidenceIntervals
    ) {
      paramChanges.showConfidenceIntervals = {
        from: workspace1.configuration.analysisParams.showConfidenceIntervals,
        to: workspace2.configuration.analysisParams.showConfidenceIntervals,
      };
    }

    if (
      workspace1.configuration.analysisParams.confidenceLevel !==
      workspace2.configuration.analysisParams.confidenceLevel
    ) {
      paramChanges.confidenceLevel = {
        from: workspace1.configuration.analysisParams.confidenceLevel,
        to: workspace2.configuration.analysisParams.confidenceLevel,
      };
    }

    if (
      workspace1.configuration.analysisParams.showCensoredData !==
      workspace2.configuration.analysisParams.showCensoredData
    ) {
      paramChanges.showCensoredData = {
        from: workspace1.configuration.analysisParams.showCensoredData,
        to: workspace2.configuration.analysisParams.showCensoredData,
      };
    }

    changes.analysisParams = paramChanges;
    const analysisChanges = compareAnalysisParams(
      workspace1.configuration.analysisParams,
      workspace2.configuration.analysisParams
    );
    changesSummary.push(...analysisChanges);
  }

  // Compare tags
  if (!deepEquals(workspace1.metadata.tags, workspace2.metadata.tags)) {
    changes.tags = {
      from: workspace1.metadata.tags,
      to: workspace2.metadata.tags,
    };
    changesSummary.push('Tags updated');
  }

  // Compare data snapshots
  const dataChanged = !deepEquals(workspace1.dataSnapshot, workspace2.dataSnapshot);
  if (dataChanged) {
    if (workspace1.dataSnapshot && workspace2.dataSnapshot) {
      if (workspace1.dataSnapshot.totalRecords !== workspace2.dataSnapshot.totalRecords) {
        changesSummary.push(
          `Data records: ${workspace1.dataSnapshot.totalRecords} → ${workspace2.dataSnapshot.totalRecords}`
        );
      }
      if (workspace1.dataSnapshot.checksum !== workspace2.dataSnapshot.checksum) {
        changesSummary.push('Data content changed');
      }
    } else if (workspace1.dataSnapshot && !workspace2.dataSnapshot) {
      changesSummary.push('Data snapshot removed');
    } else if (!workspace1.dataSnapshot && workspace2.dataSnapshot) {
      changesSummary.push('Data snapshot added');
    }
  }

  const hasChanges = Object.keys(changes).length > 0 || dataChanged;

  return {
    hasChanges,
    changes,
    filtersChanged,
    parametersChanged,
    dataChanged,
    metadataChanged: !!(changes.name || changes.description || changes.tags),
    changesSummary,
  };
}

/**
 * Compare filter configurations and return human-readable changes
 */
function compareFilters(filters1: FailureFilters, filters2: FailureFilters): string[] {
  const changes: string[] = [];

  // Compare multi-select filters
  const multiSelectFields = [
    'sites',
    'turbineModels',
    'componentsMajor',
    'componentsMinor',
    'outcomeName',
    'failureModes',
    'excludedCaseIds',
  ] as const;

  for (const field of multiSelectFields) {
    const arr1 = filters1[field] || [];
    const arr2 = filters2[field] || [];

    if (!arraysEqual(arr1, arr2)) {
      const fieldLabel = getFilterFieldLabel(field);
      const added = arr2.filter((item) => !arr1.includes(item));
      const removed = arr1.filter((item) => !arr2.includes(item));

      if (added.length > 0) {
        changes.push(`${fieldLabel}: Added ${added.length} item(s)`);
      }
      if (removed.length > 0) {
        changes.push(`${fieldLabel}: Removed ${removed.length} item(s)`);
      }
    }
  }

  // Compare numeric ranges
  if (
    filters1.operationTimeMin !== filters2.operationTimeMin ||
    filters1.operationTimeMax !== filters2.operationTimeMax
  ) {
    changes.push('Operation time range changed');
  }

  // Compare date ranges
  if (filters1.dateFrom !== filters2.dateFrom || filters1.dateTo !== filters2.dateTo) {
    changes.push('Date range changed');
  }

  return changes;
}

/**
 * Compare analysis parameters and return human-readable changes
 */
function compareAnalysisParams(
  params1: Workspace['configuration']['analysisParams'],
  params2: Workspace['configuration']['analysisParams']
): string[] {
  const changes: string[] = [];

  if (params1.showConfidenceIntervals !== params2.showConfidenceIntervals) {
    changes.push(
      `Confidence intervals: ${params1.showConfidenceIntervals ? 'enabled' : 'disabled'} → ${params2.showConfidenceIntervals ? 'enabled' : 'disabled'}`
    );
  }

  if (params1.confidenceLevel !== params2.confidenceLevel) {
    changes.push(`Confidence level: ${params1.confidenceLevel}% → ${params2.confidenceLevel}%`);
  }

  if (params1.showCensoredData !== params2.showCensoredData) {
    changes.push(
      `Censored data: ${params1.showCensoredData ? 'shown' : 'hidden'} → ${params2.showCensoredData ? 'shown' : 'hidden'}`
    );
  }

  return changes;
}

/**
 * Get human-readable label for filter field
 */
function getFilterFieldLabel(field: string): string {
  const labels: Record<string, string> = {
    sites: 'Sites',
    turbineModels: 'Turbine Models',
    componentsMajor: 'Components',
    componentsMinor: 'Sub-components',
    outcomeName: 'Failure Types',
    failureModes: 'Failure Modes',
    excludedCaseIds: 'Excluded Cases',
  };
  return labels[field] || field;
}

/**
 * Generate automatic change summary from workspace comparison
 */
export function generateAutoChangesSummary(
  changesOrOldWorkspace: WorkspaceComparison['changes'] | Workspace,
  newWorkspace?: Workspace
): string {
  let changes: WorkspaceComparison['changes'];

  // Handle both signatures: generateAutoChangesSummary(changes) and generateAutoChangesSummary(oldWorkspace, newWorkspace)
  if (newWorkspace) {
    // Two workspace arguments - compare them
    const comparison = compareWorkspaces(changesOrOldWorkspace as Workspace, newWorkspace);
    changes = comparison.changes;

    if (!comparison.hasChanges) {
      return 'No changes detected';
    }
  } else {
    // Single changes object argument
    changes = changesOrOldWorkspace as WorkspaceComparison['changes'];

    if (!changes || Object.keys(changes).length === 0) {
      return 'No changes detected';
    }
  }

  const summaryParts: string[] = [];

  // Handle name changes
  if (changes.name) {
    summaryParts.push(`Name changed from "${changes.name.from}" to "${changes.name.to}"`);
  }

  // Handle description changes
  if (changes.description) {
    summaryParts.push('Description updated');
  }

  // Handle analysis parameter changes
  if (changes.analysisParams) {
    const paramParts: string[] = [];
    const analysisParams = changes.analysisParams as Record<string, WorkspaceChange>;
    Object.keys(analysisParams).forEach((param) => {
      const change = analysisParams[param];
      paramParts.push(`${param}: ${change.from} → ${change.to}`);
    });
    summaryParts.push(`Analysis parameters updated (${paramParts.join(', ')})`);
  }

  // Handle filter changes
  if (changes.filters) {
    const filterParts: string[] = [];
    const filters = changes.filters as Record<string, unknown>;
    Object.keys(filters).forEach((filter) => {
      if (filter !== 'from' && filter !== 'to') {
        const change = filters[filter];
        if (change && typeof change === 'object' && 'from' in change && 'to' in change) {
          const workspaceChange = change as WorkspaceChange;
          filterParts.push(
            `${filter}: ${JSON.stringify(workspaceChange.from)} → ${JSON.stringify(workspaceChange.to)}`
          );
        }
      }
    });
    if (filterParts.length > 0) {
      summaryParts.push(`Filters updated (${filterParts.join(', ')})`);
    } else {
      summaryParts.push('Filters updated');
    }
  }

  // Handle tag changes
  if (changes.tags) {
    summaryParts.push(
      `Tags updated: ${JSON.stringify(changes.tags.from)} → ${JSON.stringify(changes.tags.to)}`
    );
  }

  if (summaryParts.length === 0) {
    return 'No changes detected';
  }

  return summaryParts.join('; ');
}

/**
 * Deep clone utility
 */
function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime()) as T;
  if (Array.isArray(obj)) return obj.map((item) => deepClone(item)) as T;

  const cloned = {} as T;
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      cloned[key] = deepClone(obj[key]);
    }
  }
  return cloned;
}

/**
 * Deep equality check for objects
 */
function deepEquals(obj1: unknown, obj2: unknown): boolean {
  if (obj1 === obj2) return true;
  if (obj1 == null || obj2 == null) return false;
  if (typeof obj1 !== typeof obj2) return false;

  if (Array.isArray(obj1)) {
    if (!Array.isArray(obj2) || obj1.length !== obj2.length) return false;
    return obj1.every((item, index) => deepEquals(item, obj2[index]));
  }

  if (typeof obj1 === 'object') {
    const keys1 = Object.keys(obj1 as Record<string, unknown>);
    const keys2 = Object.keys(obj2 as Record<string, unknown>);
    if (keys1.length !== keys2.length) return false;
    return keys1.every((key) =>
      deepEquals((obj1 as Record<string, unknown>)[key], (obj2 as Record<string, unknown>)[key])
    );
  }

  return obj1 === obj2;
}

/**
 * Array equality check
 */
function arraysEqual(arr1: unknown[], arr2: unknown[]): boolean {
  if (arr1.length !== arr2.length) return false;
  const sorted1 = [...arr1].sort();
  const sorted2 = [...arr2].sort();
  return sorted1.every((item, index) => item === sorted2[index]);
}
